import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BottomDrawer from "../../../common/drawer/bottomDrawer";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import DefaultCheckbox from "../../../../components/inputs/defaultCheckbox";
import StepIndicator from "../../../../components/stepIndicator/StepIndicator";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiCurrencyRupee,
  HiXCircle
} from "react-icons/hi";
import { CgSpinner } from "react-icons/cg";
import toast from "react-hot-toast";
import moment from "moment";
import { createPayrollRun, checkAttendanceRollup, getPayrollCycles } from "../../../../../services/workspace/payrollManagement.service";

const STEPS = [
  { id: 1, label: "Select Cycle", key: "cycle" },
  { id: 2, label: "Dry Run & Options", key: "options" },
  { id: 3, label: "Run", key: "run" },
  { id: 4, label: "Review Exceptions", key: "exceptions" },
  { id: 5, label: "Finalize & Export", key: "finalize" },
];

const PayrollRunWizard = ({ onClose, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [payrollCycles, setPayrollCycles] = useState([]);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [options, setOptions] = useState({
    include_contractors: false,
    apply_loans: true,
    generate_payslips: true,
  });
  const [runStatus, setRunStatus] = useState(null);
  const [runProgress, setRunProgress] = useState({ total: 0, processed: 0, exceptions: 0 });
  const [runResult, setRunResult] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [attendanceRollupStatus, setAttendanceRollupStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [runHash, setRunHash] = useState(null);

  useEffect(() => {
    // Set default period to current month
    const today = moment();
    const startOfMonth = today.clone().startOf("month");
    const endOfMonth = today.clone().endOf("month");
    setPeriodStart(startOfMonth.format("YYYY-MM-DD"));
    setPeriodEnd(endOfMonth.format("YYYY-MM-DD"));
    
    // Fetch payroll cycles
    fetchPayrollCycles();
  }, []);

  const fetchPayrollCycles = async () => {
    try {
      const response = await getPayrollCycles({});
      setPayrollCycles(response?.payrollCycle?.data || []);
    } catch (error) {
      console.error("Payroll cycles fetch error:", error);
      // Fallback to default cycles
      setPayrollCycles([
        { id: "1", name: "Monthly", period: "Monthly", description: "Standard monthly payroll" },
        // { id: "cycle_002", name: "Bi-weekly", period: "Bi-weekly", description: "Every two weeks" },
        // { id: "cycle_003", name: "Custom", period: "Custom", description: "Custom period" },
      ]);
    }
  };

  useEffect(() => {
    if (periodStart && periodEnd && currentStep >= 2) {
      checkAttendanceRollupStatus();
    }
  }, [periodStart, periodEnd, currentStep]);

  const checkAttendanceRollupStatus = async () => {
    try {
      const response = await checkAttendanceRollup(periodStart, periodEnd);
      // API returns: { message, data: { is_ready, records_available, total_employees, missing_records, ... } }
      const data = response?.data || response;
      
      setAttendanceRollupStatus({
        ready: data.ready ?? data.is_ready,
        records_available: data.records_available,   // employees with attendance (ab sahi hai)
        total_employees: data.total_employees,
        missing_records: data.missing_records,       // employees without any attendance
        // optional: extra fields agar use karoge
        completion_percentage: data.completion_percentage,
        expected_records: data.expected_records,
        attendance_count: data.attendance_count,
      });
    } catch (error) {
      console.error("Attendance rollup check error:", error);
      toast.error(error?.response?.data?.message || "Failed to check attendance roll-up status");
      setAttendanceRollupStatus({
        ready: false,
        missing_records: 0,
        total_employees: 0,
        records_available: 0,
      });
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!selectedCycle) {
        toast.error("Please select a payroll cycle");
        return;
      }
      if (!periodStart || !periodEnd) {
        toast.error("Please select period dates");
        return;
      }
      if (moment(periodEnd).isBefore(moment(periodStart))) {
        toast.error("End date must be after start date");
        return;
      }
    }
    if (currentStep === 2) {
      if (!attendanceRollupStatus?.ready) {
        toast.error("Attendance roll-up is not ready. Please check attendance records.");
        return;
      }
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      if (currentStep === 2) {
        // Move to run step and start the run
        setTimeout(() => {
          startPayrollRun();
        }, 100);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createSnapshot = () => {
    // Create a deterministic snapshot for reproducibility
    const snapshotData = {
      cycle_id: selectedCycle?.value,
      period_start: periodStart,
      period_end: periodEnd,
      dry_run: dryRun,
      options: { ...options },
      attendance_rollup_snapshot: {
        ready: attendanceRollupStatus?.ready || false,
        missing_records: attendanceRollupStatus?.missing_records || 0,
        total_employees: attendanceRollupStatus?.total_employees || 0,
        records_available: attendanceRollupStatus?.records_available || 0,
      },
      created_at: new Date().toISOString(),
    };
    setSnapshot(snapshotData);
    
    // Generate a deterministic hash for reproducibility
    // Sort keys to ensure consistent hash generation
    const sortedSnapshot = JSON.stringify(snapshotData, Object.keys(snapshotData).sort());
    // Use a simple hash function for reproducibility
    let hash = 0;
    for (let i = 0; i < sortedSnapshot.length; i++) {
      const char = sortedSnapshot.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const hashString = Math.abs(hash).toString(16).substring(0, 16);
    setRunHash(hashString);
    return snapshotData;
  };

  const startPayrollRun = async () => {
    try {
      setRunStatus("PROCESSING");
      const snapshotData = createSnapshot();

      // Initialize progress
      setRunProgress({ total: attendanceRollupStatus?.total_employees || 0, processed: 0, exceptions: 0 });

      // Create payroll run with snapshot for reproducibility
      const runData = {
        period_start: periodStart,
        period_end: periodEnd,
        dry_run: dryRun,
        options: { ...options },
        snapshot: snapshotData,
      };

      // Call API to create payroll run
      const response = await createPayrollRun(runData);
      
      // Update with response data
      if (response) {
        setRunResult(response);
        setRunHash(response.run_hash || runHash);
        
        // If run is already completed (for dry runs), move to exceptions
        if (response.status === "DRY_COMPLETED" || response.status === "READY_FOR_FINALIZE") {
          setExceptions(response.exceptions || []);
          setRunStatus(response.status);
          setCurrentStep(4); // Move to exceptions review
        } else {
          // For processing runs, poll for status updates
          pollRunStatus(response.run_id);
        }
      }
    } catch (error) {
      console.error("Payroll run error:", error);
      toast.error(error?.response?.data?.message || "Failed to start payroll run");
      setRunStatus("FAILED");
    }
  };

  const pollRunStatus = async (runId) => {
    // Poll for run status updates (in real app, use WebSocket)
    const pollInterval = setInterval(async () => {
      try {
        const { getPayrollRunDetail } = await import("../../../../../services/workspace/payrollManagement.service");
        const runDetail = await getPayrollRunDetail(runId);
        
        if (runDetail) {
          setRunProgress({
            total: runDetail.summary?.total_employees || 0,
            processed: runDetail.summary?.processed || 0,
            exceptions: runDetail.summary?.exceptions || 0,
          });

          if (runDetail.status === "DRY_COMPLETED" || runDetail.status === "READY_FOR_FINALIZE" || runDetail.status === "COMPLETED") {
            clearInterval(pollInterval);
            setRunResult(runDetail);
            setExceptions(runDetail.exceptions || []);
            setRunStatus(runDetail.status);
            setCurrentStep(4); // Move to exceptions review
          } else if (runDetail.status === "FAILED" || runDetail.status === "ROLLED_BACK") {
            clearInterval(pollInterval);
            setRunStatus(runDetail.status);
            toast.error("Payroll run failed");
          }
        }
      } catch (error) {
        console.error("Poll error:", error);
        clearInterval(pollInterval);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  const refreshExceptions = useCallback(async () => {
    const runId = runResult?.run_id || runResult?.id;
    if (!runId) return;
    try {
      setExceptionsLoading(true);
      const { getPayrollRunExceptions } = await import(
        "../../../../../services/workspace/payrollManagement.service"
      );
      const updated = await getPayrollRunExceptions(runId, {});
      const list = updated?.data || updated?.exceptions || updated || [];
      setExceptions(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load exceptions");
    } finally {
      setExceptionsLoading(false);
    }
  }, [runResult?.run_id, runResult?.id]);

  // When user reaches "Review Exceptions", ensure we load the real exception list.
  useEffect(() => {
    const step = Number(currentStep) || 1;
    if (step !== 4) return;
    const expected =
      Number(
        runResult?.unresolved_exceptions ??
          runResult?.summary?.unresolved_exceptions ??
          runResult?.summary?.exceptions ??
          0
      ) || 0;
    if (expected > 0 && (!exceptions || exceptions.length === 0) && !exceptionsLoading) {
      refreshExceptions();
    }
  }, [currentStep, runResult, exceptions, exceptionsLoading, refreshExceptions]);

  const handleFinalize = async () => {
    const prevStatus = runStatus;
    try {
      setRunStatus("FINALIZING");
      
      // Check if there are unresolved exceptions
      const unresolvedExceptions = exceptions.filter(exc => !exc.resolved);
      if (unresolvedExceptions.length > 0) {
        toast.error(
          `Please resolve exceptions before finalizing (${unresolvedExceptions.length} pending).`
        );
        setRunStatus(prevStatus || runResult?.status);
        return;
      }

      // Atomic finalization - all or nothing
      const { finalizePayrollRun } = await import("../../../../../services/workspace/payrollManagement.service");
      const finalizeData = {
        export_options: {
          payslips: true,
          bank_csv: true,
          statutory_reports: true,
        },
      };

      const response = await finalizePayrollRun(runResult.run_id || runResult.id, finalizeData);
      
      if (response) {
        setRunStatus("COMPLETED");
        toast.success("Payroll run finalized successfully!");
        
        // Close wizard and navigate
        if (onComplete) {
          onComplete(runResult.run_id || runResult.id);
        } else {
          navigate(`/hr/payroll-runs/${runResult.run_id || runResult.id}`);
        }
      }
    } catch (error) {
      console.error("Finalize error:", error);
      toast.error(error?.response?.data?.message || "Failed to finalize payroll run.");
      // Restore previous status so user can retry without browser alerts.
      setRunStatus(prevStatus || runResult?.status);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <CycleSelectionStep 
          selectedCycle={selectedCycle}
          setSelectedCycle={setSelectedCycle}
          periodStart={periodStart}
          setPeriodStart={setPeriodStart}
          periodEnd={periodEnd}
          setPeriodEnd={setPeriodEnd}
          payrollCycles={payrollCycles}
        />;
      case 2:
        return <OptionsStep 
          dryRun={dryRun}
          setDryRun={setDryRun}
          options={options}
          setOptions={setOptions}
          attendanceRollupStatus={attendanceRollupStatus}
        />;
      case 3:
        return <RunStep 
          runStatus={runStatus}
          runProgress={runProgress}
          runResult={runResult}
          snapshot={snapshot}
          runHash={runHash}
        />;
      case 4:
        return <ExceptionsStep 
          exceptions={exceptions}
          runResult={runResult}
          expectedExceptionsCount={
            Number(
              runResult?.unresolved_exceptions ??
                runResult?.summary?.unresolved_exceptions ??
                runResult?.summary?.exceptions ??
                0
            ) || 0
          }
          loading={exceptionsLoading}
          onRefresh={refreshExceptions}
          onBulkFix={async () => {
            try {
              const isResolved = (exc) =>
                exc?.resolved === true ||
                exc?.is_resolved === true ||
                exc?.is_resolved === 1 ||
                !!exc?.resolved_at ||
                (typeof exc?.status === "string" && exc.status.toUpperCase() === "RESOLVED");

              const selectedExceptions = (exceptions || []).filter((exc) => !isResolved(exc));
              if (selectedExceptions.length === 0) {
                toast.error("No exceptions selected for bulk fix");
                return;
              }
              
              const { bulkFixExceptions } = await import("../../../../../services/workspace/payrollManagement.service");
              const result = await bulkFixExceptions(runResult.run_id || runResult.id, {
                exception_ids: selectedExceptions.map(exc => exc.id),
                action: "mark_excused",
                reason: "Bulk fixed via wizard",
              });
              
              if (result) {
                toast.success(`${result.fixed_count || selectedExceptions.length} exceptions fixed`);
                await refreshExceptions();
              }
            } catch (error) {
              toast.error(error?.response?.data?.message || "Failed to bulk fix exceptions");
            }
          }}
        />;
      case 5:
        return <FinalizeStep 
          runResult={runResult}
          onFinalize={handleFinalize}
          runStatus={runStatus}
        />;
      default:
        return null;
    }
  };

  const handleDrawerClose = (isOpen) => {
    if (!isOpen && onClose) {
      onClose();
    }
  };

  // Defensive: currentStep should be a number, but normalize just in case.
  const step = Number(currentStep) || 1;

  return (
    <BottomDrawer
      isOpen={true}
      setIsOpen={handleDrawerClose}
      title="Payroll Run Wizard"
      size="md"
      position="right"
    >
      <div className="flex flex-col gap-6">
        {/* Stepper */}
        <StepIndicator steps={STEPS} currentStep={currentStep} />

        {/* Step Content */}
        <div className="min-h-[400px]">{renderStepContent()}</div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t border-borderColor">
          <DefaultButton
            name="Back"
            variant="outline"
            onClick={handleBack}
            disabled={step === 1 || runStatus === "PROCESSING" || runStatus === "FINALIZING"}
          />
          <div className="flex gap-2">
            {step < STEPS.length && step !== 3 && step !== 4 && (
              <DefaultButton
                name={step === STEPS.length - 1 ? "Complete" : "Next"}
                onClick={handleNext}
                disabled={
                  (step === 1 && (!selectedCycle || !periodStart || !periodEnd)) ||
                  (step === 2 && !attendanceRollupStatus?.ready)
                }
              />
            )}
            {step === 3 &&
              ["DRY_COMPLETED", "READY_FOR_FINALIZE", "COMPLETED"].includes(String(runStatus || "").toUpperCase()) && (
                <DefaultButton name="Next" onClick={handleNext} />
              )}
            {step === 4 && dryRun && (
              <>
                <DefaultButton
                  name="Complete"
                  variant="success"
                  onClick={() => {
                    // Close wizard for dry run - just complete without finalizing
                    if (onComplete) {
                      onComplete(runResult?.run_id || runResult?.id);
                    } else {
                      navigate(`/hr/payroll-runs/${runResult?.run_id || runResult?.id}`);
                    }
                    if (onClose) {
                      onClose();
                    }
                  }}
                />
                {(exceptions?.length ?? 0) === 0 && (
                  <DefaultButton
                    name="Finalize Run"
                    onClick={() => {
                      setDryRun(false);
                      handleNext();
                    }}
                  />
                )}
              </>
            )}
            {/* Review Exceptions (Non-dry run): proceed to finalization */}
            {step === 4 && !dryRun && (
              <DefaultButton
                name="Next"
                onClick={handleNext}
                disabled={exceptionsLoading}
              />
            )}
            {step === 5 && (
              <DefaultButton
                name="Finalize & Export"
                onClick={handleFinalize}
                disabled={runStatus === "FINALIZING"}
              />
            )}
          </div>
        </div>
      </div>
    </BottomDrawer>
  );
};

// Step Components
const CycleSelectionStep = ({ selectedCycle, setSelectedCycle, periodStart, setPeriodStart, periodEnd, setPeriodEnd, payrollCycles }) => {
  const cycleOptions = (payrollCycles || []).map((cycle) => ({
    value: cycle.id,
    label: cycle.name,
    description: cycle.description || cycle.period,
  }));

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-gray-900">Select Payroll Cycle</h3>
      <div>
        <label className="block text-sm font-medium text-theme mb-2">
          Payroll Cycle
        </label>
        <DefaultSearchSelectInput
          options={cycleOptions}
          value={selectedCycle}
          onChange={setSelectedCycle}
          placeholder="Select a payroll cycle"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-theme mb-2">
            Period Start
          </label>
          <DefaultDatePicker
            value={periodStart}
            onChange={(date) => setPeriodStart(date)}
            placeholder="Select start date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-theme mb-2">
            Period End
          </label>
          <DefaultDatePicker
            value={periodEnd}
            onChange={(date) => setPeriodEnd(date)}
            placeholder="Select end date"
          />
        </div>
      </div>
    </div>
  );
};

const OptionsStep = ({ dryRun, setDryRun, options, setOptions, attendanceRollupStatus }) => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-gray-900">Dry Run & Options</h3>
      
      <div className="bg-primary-50 p-4 rounded-lg border border-borderColor">
        <div className="flex items-center justify-between mb-4">
          <div>
            <label className="text-sm font-medium text-theme">Dry Run</label>
            <p className="text-xs text-textLight">
              Run payroll without finalizing. Results can be reviewed before finalizing.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-theme"></div>
          </label>
        </div>
      </div>
{/* 
      <div className="space-y-3">
        <DefaultCheckbox
          label="Include Contractors"
          checked={options.include_contractors}
          onChange={(e) => setOptions({ ...options, include_contractors: e.target.checked })}
        />
        <DefaultCheckbox
          label="Apply Loans & Advances"
          checked={options.apply_loans}
          onChange={(e) => setOptions({ ...options, apply_loans: e.target.checked })}
        />
        <DefaultCheckbox
          label="Generate Payslips"
          checked={options.generate_payslips}
          onChange={(e) => setOptions({ ...options, generate_payslips: e.target.checked })}
        />
      </div> */}

      {attendanceRollupStatus && (
        <div className={`p-4 rounded-lg border ${
          attendanceRollupStatus.ready
            ? "bg-success-50 border-success-200"
            : "bg-error-50 border-error-200"
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {attendanceRollupStatus.ready ? (
              <HiCheckCircle className="w-5 h-5 text-success-600" />
            ) : (
              <HiExclamationCircle className="w-5 h-5 text-error-600" />
            )}
            <span className={`font-medium ${
              attendanceRollupStatus.ready ? "text-success-700" : "text-error-700"
            }`}>
              Attendance Roll-up Status
            </span>
          </div>
          <p className="text-sm text-theme">
            {attendanceRollupStatus.ready
              ? `Ready: ${attendanceRollupStatus.records_available || 0}/${attendanceRollupStatus.total_employees || 0} employees have attendance records.`
              : `Not Ready: ${attendanceRollupStatus.missing_records || 0} employee${(attendanceRollupStatus.missing_records || 0) !== 1 ? 's' : ''} missing attendance records.`}
          </p>
        </div>
      )}
    </div>
  );
};

const RunStep = ({ runStatus, runProgress, runResult, snapshot, runHash }) => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-gray-900">Running Payroll</h3>
      
      {runStatus === "PROCESSING" && (
        <div className="flex flex-col items-center gap-4 py-8">
          <CgSpinner className="w-12 h-12 text-theme animate-spin" />
          <div className="text-center">
            <p className="text-theme font-medium mb-2">Processing payroll...</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-theme h-2.5 rounded-full transition-all duration-300"
                style={{
                  width: `${(runProgress.processed / runProgress.total) * 100}%`,
                }}
              ></div>
            </div>
            <p className="text-sm text-textLight mt-2">
              {runProgress.processed} / {runProgress.total} employees processed
            </p>
          </div>
        </div>
      )}

      {runResult && (
        <div className="space-y-4">
          <div className="bg-success-50 p-4 rounded-lg border border-success-200">
            <div className="flex items-center gap-2 mb-2">
              <HiCheckCircle className="w-5 h-5 text-success-600" />
              <span className="font-medium text-success-700">Run Completed</span>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <p className="text-xs text-textLight">Total Employees</p>
                <p className="text-base font-semibold text-gray-900">{runResult.summary.total_employees}</p>
              </div>
              <div>
                <p className="text-xs text-textLight">Processed</p>
                <p className="text-lg font-semibold text-success-600">{runResult.summary.processed}</p>
              </div>
              <div>
                <p className="text-xs text-textLight">Exceptions</p>
                <p className="text-lg font-semibold text-error-600">{runResult.summary.exceptions}</p>
              </div>
            </div>
          </div>

          {snapshot && (
            <div className="bg-primary-50 p-4 rounded-lg border border-borderColor">
              <p className="text-xs font-medium text-theme mb-2">Run Snapshot (for reproducibility)</p>
              <p className="text-xs text-textLight break-all">
                Hash: {runHash}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ExceptionsStep = ({
  exceptions,
  runResult,
  onBulkFix,
  onRefresh,
  loading = false,
  expectedExceptionsCount = 0,
}) => {
  const getExceptionTypeLabel = (type) => {
    const labels = {
      MISSING_BANK: "Missing Bank Details",
      MISSING_SALARY_STRUCTURE: "Missing Salary Structure",
      MISSING_STATUTORY_ID: "Missing Statutory ID",
      NEGATIVE_NET_PAY: "Negative Net Pay",
      VALIDATION_ERROR: "Validation Error",
    };
    return labels[type] || type;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      HIGH: "bg-error-100 text-error-700",
      MEDIUM: "bg-warning-100 text-warning-700",
      LOW: "bg-information-100 text-information-700",
    };
    return colors[severity] || colors.MEDIUM;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Review Exceptions</h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onRefresh}
            className="text-sm text-theme hover:text-themeHover font-medium"
          >
            Refresh
          </button>
          {exceptions.length > 0 && (
            <button
              type="button"
              onClick={onBulkFix}
              className="text-sm text-theme hover:text-themeHover font-medium"
            >
              Bulk Fix
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-textLight">Loading exceptions...</div>
      ) : exceptions.length === 0 ? (
        <div className="bg-success-50 p-6 rounded-lg border border-success-200 text-center">
          <HiCheckCircle className="w-12 h-12 text-success-600 mx-auto mb-2" />
          <p className="text-success-700 font-medium">No exceptions found!</p>
          {expectedExceptionsCount > 0 ? (
            <p className="text-sm text-success-600 mt-1">
              Backend indicates {expectedExceptionsCount} exception(s). Click Refresh to load them.
            </p>
          ) : (
            <p className="text-sm text-success-600 mt-1">All employees are ready for payroll.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4 max-h-60 overflow-y-auto">
          {exceptions.map((exception) => (
            <div
              key={exception.id}
              className="bg-white p-4 rounded-lg border border-borderColor hover:bg-primary-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(exception.severity)}`}>
                      {exception.severity}
                    </span>
                    <span className="text-sm font-medium text-theme">
                      {getExceptionTypeLabel(exception.type)}
                    </span>
                  </div>
                  <p className="text-sm text-theme font-medium">{exception.employee_name}</p>
                  <p className="text-xs text-textLight mt-1">{exception.message}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const { bulkFixExceptions } = await import("../../../../../services/workspace/payrollManagement.service");
                      await bulkFixExceptions(runResult.run_id || runResult.id, {
                        exception_ids: [exception.id],
                        action: "fix",
                        reason: "Fixed via wizard",
                      });
                      toast.success("Exception fixed");
                      await onRefresh?.();
                    } catch (error) {
                      toast.error(error?.response?.data?.message || "Failed to fix exception");
                    }
                  }}
                  className="text-sm text-theme hover:text-themeHover font-medium"
                >
                  Fix
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {runResult && (
        <div className="bg-primary-50 p-4 rounded-lg border border-borderColor">
          <p className="text-sm font-medium text-theme mb-2">Run Summary</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-textLight">Total Gross</p>
              <p className="text-base font-semibold text-gray-900">
                ₹{runResult.summary.total_gross?.toLocaleString("en-IN")}
              </p>
            </div>
            <div>
              <p className="text-xs text-textLight">Total Net</p>
              <p className="text-lg font-semibold text-success-600">
                ₹{runResult.summary.total_net?.toLocaleString("en-IN")}
              </p>
            </div>
            <div>
              <p className="text-xs text-textLight">Total Deductions</p>
              <p className="text-lg font-semibold text-error-600">
                ₹{runResult.summary.total_deductions?.toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FinalizeStep = ({ runResult, onFinalize, runStatus }) => {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-gray-900">Finalize & Export</h3>
      
      <div className="bg-warning-50 p-4 rounded-lg border border-warning-200">
        <div className="flex items-start gap-2">
          <HiExclamationCircle className="w-5 h-5 text-warning-600 mt-0.5" />
          <div>
            <p className="font-medium text-warning-700 mb-1">Finalizing payroll is irreversible</p>
            <p className="text-sm text-warning-600">
              Once finalized, the payroll will be marked as COMPLETED and all exports will be generated.
            </p>
          </div>
        </div>
      </div>

      {runResult && (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border border-borderColor">
            <p className="text-sm font-medium text-theme mb-3">Export Options</p>
            <div className="space-y-2">
              <DefaultCheckbox label="Payslips (ZIP of PDFs)" defaultChecked />
              <DefaultCheckbox label="Bank CSV File" defaultChecked />
              <DefaultCheckbox label="Statutory Reports (PF/ESI/PT/24Q)" defaultChecked />
            </div>
          </div>

          <div className="bg-primary-50 p-4 rounded-lg border border-borderColor">
            <p className="text-sm font-medium text-theme mb-2">Final Summary</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-textLight">Total Employees</p>
                <p className="text-base font-semibold text-gray-900">{runResult.summary.total_employees}</p>
              </div>
              <div>
                <p className="text-xs text-textLight">Total Net Pay</p>
                <p className="text-lg font-semibold text-success-600">
                  ₹{runResult.summary.total_net?.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {runStatus === "FINALIZING" && (
        <div className="flex items-center justify-center gap-2 py-4">
          <CgSpinner className="w-5 h-5 text-theme animate-spin" />
          <p className="text-theme">Finalizing payroll...</p>
        </div>
      )}
    </div>
  );
};

export default PayrollRunWizard;

